import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * i18n-backed replacement for the previous `STRINGS` constant.
 *
 * Returns the same object shape as the old `strings.uz.ts` so consuming
 * components can keep using `STRINGS.X` (with a small import swap).
 *
 * Keys live under `customer:home.*` in the locale JSONs.
 */
export function useHomeStrings() {
  const { t } = useTranslation('customer');

  return useMemo(() => ({
    // Status
    STATUS_OPEN: t('home.status.open'),
    STATUS_CLOSED: t('home.status.closed'),

    // Hero
    NEXT_WORKING_DAY_PREFIX: t('home.hero.nextWorkingDayPrefix'),
    HERO_TODAY_PREFIX: t('home.hero.todayPrefix'),

    // Upcoming booking
    UPCOMING_TITLE: t('home.upcoming.title'),
    UPCOMING_SECTION_LABEL: t('home.upcoming.sectionLabel'),
    UPCOMING_EMPTY_TITLE: t('home.upcoming.emptyTitle'),
    UPCOMING_EMPTY_DESC: t('home.upcoming.emptyDesc'),
    BOOKING_STATUS_CONFIRMED: t('home.bookingStatus.CONFIRMED'),
    BOOKING_STATUS_PENDING: t('home.bookingStatus.PENDING'),
    BOOKING_DETAILS: t('home.bookingActions.details'),
    BOOKING_CANCEL: t('home.bookingActions.cancelRequest'),
    BOOKING_LIST: t('home.bookingActions.list'),

    // Cancel modal
    CANCEL_MODAL_TITLE: t('home.cancelModal.title'),
    CANCEL_REASON_LABEL: t('home.cancelModal.reasonLabel'),
    CANCEL_REASON_PLACEHOLDER: t('home.cancelModal.reasonPlaceholder'),
    CANCEL_NOTE_LABEL: t('home.cancelModal.noteLabel'),
    CANCEL_NOTE_PLACEHOLDER: t('home.cancelModal.notePlaceholder'),
    CANCEL_SUBMIT: t('home.cancelModal.submit'),
    CANCEL_CLOSE: t('home.cancelModal.close'),
    CANCEL_SUCCESS: t('home.cancelModal.success'),
    CANCEL_ERROR_ALREADY_PENDING: t('home.cancelModal.errorAlreadyPending'),
    CANCEL_ERROR_NOT_FOUND: t('home.cancelModal.errorNotFound'),
    CANCEL_ERROR_FORBIDDEN: t('home.cancelModal.errorForbidden'),
    CANCEL_ERROR_GENERIC: t('home.cancelModal.errorGeneric'),

    // Cancel reasons
    REASON_PLANS_CHANGED: t('home.cancelReasons.plansChanged'),
    REASON_NEED_DIFFERENT_TIME: t('home.cancelReasons.needDifferentTime'),
    REASON_CANNOT_ATTEND: t('home.cancelReasons.cannotAttend'),
    REASON_OTHER: t('home.cancelReasons.other'),

    // Services
    SERVICES_TITLE: t('home.services.title'),
    SERVICES_SEE_ALL: (n: number) => t('home.services.seeAll', { count: n }),
    DURATION_MINUTES: (min: number) => t('home.services.durationMinutes', { count: min }),
    PRICE_HIDDEN: '',

    // Providers
    PROVIDERS_TITLE: t('home.providers.title'),
    SPECIALISTS_TITLE: t('home.providers.specialistsTitle'),
    NEXT_SLOT: (slot: string) => t('home.providers.nextSlot', { slot }),

    // Business info
    BUSINESS_INFO_TITLE: t('home.businessInfo.title'),
    SCHEDULE_TITLE: t('home.businessInfo.scheduleTitle'),
    SCHEDULE_VIEW: t('home.hero.scheduleView'),
    WEEKLY_SCHEDULE_TITLE: t('home.hero.weeklyScheduleTitle'),
    CONTACT_TITLE: t('home.businessInfo.contactTitle'),
    PHONE_CALL: t('home.businessInfo.phoneCall'),
    OPEN_MAP: t('home.businessInfo.openMap'),

    // Day names
    DAY_MONDAY: t('home.days.MONDAY'),
    DAY_TUESDAY: t('home.days.TUESDAY'),
    DAY_WEDNESDAY: t('home.days.WEDNESDAY'),
    DAY_THURSDAY: t('home.days.THURSDAY'),
    DAY_FRIDAY: t('home.days.FRIDAY'),
    DAY_SATURDAY: t('home.days.SATURDAY'),
    DAY_SUNDAY: t('home.days.SUNDAY'),
    DAY_CLOSED: t('home.days.CLOSED'),

    // Policies
    POLICIES_CANCELLATION: t('home.policies.cancellation'),
    POLICIES_LATENESS: t('home.policies.lateness'),
    POLICIES_PAYMENT: t('home.policies.payment'),

    // CTA
    BOOK_NOW: t('home.cta.bookNow'),
    BOOKING_DISABLED: t('home.cta.bookingDisabled'),

    // Errors
    ERROR_LOAD: t('home.errors.loadError'),
    RETRY: t('home.errors.retry'),
    OFFLINE_BANNER: t('home.errors.offlineBanner'),
  }), [t]);
}
